import { IconDefinition, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { twMerge } from 'tailwind-merge';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

export interface ButtonProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    title: string;
    action?: (arg?: any) => void;
    nosubmit?: boolean;
    icon?: IconDefinition;
    iconright?: boolean;
    secondary?: boolean;
    disabled?: boolean;
    noTitle?: boolean;
    active?: boolean;
    tabIndex?: number;
    className?: string;
    sizeIcon?: SizeProp;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const {
        title,
        action,
        className,
        secondary,
        active,
        sizeIcon = '1x',
        disabled,
        nosubmit,
        iconright,
        icon,
        noTitle,
        tabIndex,
        ...restProps
    } = props;

    const spinning = icon === faSpinner;

    const internalButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => internalButtonRef.current as HTMLButtonElement);

    const [sizeClass, setSizeClass] = useState('snow-button');
    const today = new Date();
    const isWinter = (today.getMonth() === 11) || (today.getMonth() === 0 && today.getDate() <= 5);

    useEffect(() => {
        if (!internalButtonRef.current || !isWinter) return;

        const { offsetWidth } = internalButtonRef.current;
        const random = Math.random() < 0.5;
        if (offsetWidth < 130) {
            return setSizeClass(random ? 'snow-button-1' : 'snow-button-3');
        }
        setSizeClass(offsetWidth < 150 ? random ? 'snow-button-2' : 'snow-button-5' : offsetWidth < 250 ? 'snow-button' : 'snow-button-4');
    }, []);

    return (
        <button
            tabIndex={disabled ? -1 : tabIndex || 0}
            ref={internalButtonRef}
            type={nosubmit ? 'button' : 'submit'}
            {...restProps}
            className={twMerge(`
                rounded-[0.4rem] flex cursor-pointer transition-all duration-300 ease-in-out py-2 justify-center gap-4 items-center relative focus:outline-none focus:ring-2 focus:ring-accent-light
                ${noTitle ? 'px-2' : 'px-6'}
                ${disabled && 'pointer-events-none opacity-40'}
                ${sizeClass}
                ${active ?
                    (secondary ?
                        'bg-light-100 border-2 text-gray-700 border-accent-light' :
                        'text-white bg-accent-light') :
                    (secondary ?
                        'hover:bg-light-100 border-2 text-gray-700 border-accent-light' :
                        'text-white bg-accent hover:bg-accent-light')}
                ${className}
            `)}
            onClick={(e) => {
                if (props.action) props.action(e);
            }}
        >
            {icon && !iconright && <FontAwesomeIcon size={sizeIcon} className={spinning ? 'animate-spin' : ''} icon={icon} />}
            {noTitle ? null : title}
            {icon && iconright && <FontAwesomeIcon size={sizeIcon} className={spinning ? 'animate-spin' : ''} icon={icon} />}
        </button>
    );
});

export default Button;
